import { useEffect, useMemo, useRef } from "react";
import { Vaga } from "@/types/vaga";
import { VagaCard } from "./VagaCard";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useVagaColumn } from "@/hooks/use-vaga-column";
import { useVagasCounts } from "@/hooks/use-vagas-counts";
import { useUserPermissions } from "@/hooks/use-user-permissions";
import { usePropostasPorProjeto } from "@/hooks/use-finance-propostas";
import { useRascunhosCancelados } from "@/hooks/use-projeto-rascunho";
import type { ControleProjetoStatus } from "@/types/controle-projeto";

interface KanbanColumnProps {
  status: Vaga["status"];
  agenciaId: string;
  title: string;
  onDragStart: (e: React.DragEvent, vagaId: string) => void;
  onDrop: (e: React.DragEvent) => void;
  onVagaClick: (vagaId: string) => void;
  onResponsavelChange: (vagaId: string, responsavel: string) => void;
  onEdit?: (vagaId: string) => void;
  onDelete?: (vagaId: string) => void;
  onMoveToStatus?: (vagaId: string, status: Vaga["status"]) => void;
  onRequestReprovacao?: (vagaId: string) => void;
  onRequestAprovacao?: (vagaId: string) => void;
  onEditProjeto?: (vagaId: string) => void;
  usuarios: { id: string; full_name?: string; username?: string }[];
}

export function KanbanColumn({
  status,
  agenciaId,
  title,
  onDragStart,
  onDrop,
  onVagaClick,
  onResponsavelChange,
  onEdit,
  onDelete,
  onMoveToStatus,
  onRequestReprovacao,
  onRequestAprovacao,
  onEditProjeto,
  usuarios,
}: KanbanColumnProps) {
  // Cada coluna carrega só a sua etapa, paginada (scroll infinito). Antes a
  // página /vagas trazia TODAS as ~527 vagas de uma vez — não escalava.
  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useVagaColumn(status, agenciaId);

  const vagas = useMemo(
    () => data?.pages.flatMap((p) => p.rows) ?? [],
    [data],
  );

  // Só a coluna Aprovado tem projeto vinculado. Uma ÚNICA busca em lote pra
  // toda a página carregada (não uma por card) — só dispara pra quem tem a
  // tela `financeiro`.
  const { screens } = useUserPermissions();
  const temFinanceiro = screens.includes("financeiro");
  const projetoIdsAprovados = useMemo(
    () =>
      status === "aprovado"
        ? vagas.map((v) => v.projeto_id).filter((id): id is string => !!id)
        : [],
    [vagas, status],
  );
  const propostasPorProjetoQuery = usePropostasPorProjeto(
    projetoIdsAprovados,
    temFinanceiro && status === "aprovado",
  );
  // Vaga aprovada sem projeto vivo: o rascunho cancelado aparece no card, com a volta para rascunho.
  const vagaIdsSemProjeto = useMemo(
    () => (status === "aprovado" ? vagas.filter((v) => !v.projeto_id).map((v) => v.id) : []),
    [vagas, status],
  );
  const { data: rascunhosCancelados } = useRascunhosCancelados(vagaIdsSemProjeto, status === "aprovado");
  // Total da coluna vem do RPC de contagem (compartilhado entre as 7 colunas),
  // não de count=exact por coluna. Fallback no carregado enquanto não chega.
  const { data: counts } = useVagasCounts(agenciaId);
  const total = counts?.[status] ?? vagas.length;

  // Scroll infinito: observa um sentinel no fim da lista.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: scrollRef.current, rootMargin: "300px" },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div
      className="shrink-0 flex flex-col w-[320px] h-[calc(100vh-8rem)] relative"
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
    >
      {/* Header da Coluna */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-xs border border-slate-200 rounded-t-xl p-4 pb-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-base text-slate-800 truncate">{title}</h3>
          <Badge
            variant="secondary"
            className="bg-slate-100 text-slate-700 text-xs font-medium px-2 py-1 min-w-[24px] justify-center"
          >
            {total}
          </Badge>
        </div>
      </div>

      {/* Container dos Cards */}
      <div className="flex-1 bg-slate-50/50 border-x border-slate-200 overflow-hidden">
        <div
          ref={scrollRef}
          className="h-full overflow-y-auto p-3 space-y-2.5 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent"
        >
          {isLoading ? (
            <>
              <Skeleton className="h-28 w-full rounded-lg" />
              <Skeleton className="h-28 w-full rounded-lg" />
              <Skeleton className="h-28 w-full rounded-lg" />
            </>
          ) : vagas.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-slate-400 text-sm">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-slate-100 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-dashed border-slate-300 rounded" />
                </div>
                Nenhuma vaga
              </div>
            </div>
          ) : (
            <>
              {vagas.map((vaga) => {
                // Reconstrói os objetos esperados pelo VagaCard a partir dos
                // campos achatados da view v_vagas_listagem (M103).
                const metricas =
                  vaga.qtd_shortlist !== undefined
                    ? {
                        vaga_id: vaga.id,
                        qtd_selecionados_kanban: vaga.qtd_selecionados_kanban ?? 0,
                        qtd_shortlist: vaga.qtd_shortlist ?? 0,
                        qtd_aprovados_kanban: vaga.qtd_aprovados_kanban ?? 0,
                        qtd_reprovados_kanban: vaga.qtd_reprovados_kanban ?? 0,
                        qtd_selecionados_agencia: vaga.qtd_selecionados_agencia ?? 0,
                        qtd_enviados_agencia: vaga.qtd_enviados_agencia ?? 0,
                        negociacao_por_status: vaga.negociacao_por_status ?? {},
                        negociacao_detalhes: vaga.negociacao_detalhes ?? [],
                      }
                    : undefined;
                const projeto = vaga.projeto_id
                  ? {
                      id: vaga.projeto_id,
                      status: (vaga.projeto_status ?? "rascunho") as ControleProjetoStatus,
                      valor_projeto: vaga.projeto_valor,
                      data_inicio: vaga.projeto_data_inicio,
                      freelancer_id: vaga.projeto_freelancer_id,
                      freelancer_nome: vaga.projeto_freelancer_nome,
                    }
                  : undefined;
                const proposta =
                  vaga.projeto_id && propostasPorProjetoQuery.data
                    ? (propostasPorProjetoQuery.data.items[vaga.projeto_id] ?? null)
                    : undefined;
                return (
                  <VagaCard
                    key={vaga.id}
                    vaga={vaga}
                    onDragStart={onDragStart}
                    onClick={() => onVagaClick(vaga.id)}
                    onResponsavelChange={(responsavel) => onResponsavelChange(vaga.id, responsavel)}
                    onEdit={onEdit ? () => onEdit(vaga.id) : undefined}
                    onDelete={onDelete ? () => onDelete(vaga.id) : undefined}
                    onMoveToStatus={onMoveToStatus ? (vagaId, st) => onMoveToStatus(vagaId, st) : undefined}
                    onRequestReprovacao={onRequestReprovacao}
                    onRequestAprovacao={onRequestAprovacao}
                    onEditProjeto={onEditProjeto}
                    usuarios={usuarios}
                    metricas={metricas}
                    proposta={proposta}
                    projeto={projeto}
                    rascunhoCancelado={vaga.projeto_id ? undefined : rascunhosCancelados?.get(vaga.id)}
                    freelancerAprovadoKanbanId={vaga.aprovado_kanban_freelancer_id ?? null}
                    freelancerAprovadoKanbanNome={vaga.aprovado_kanban_freelancer_nome ?? null}
                  />
                );
              })}
              {/* Sentinel + indicador de "carregando mais" (scroll infinito) */}
              <div ref={sentinelRef} />
              {isFetchingNextPage && <Skeleton className="h-28 w-full rounded-lg" />}
            </>
          )}
        </div>
      </div>

      {/* Footer da Coluna */}
      <div className="bg-white/80 backdrop-blur-xs border border-slate-200 border-t-0 rounded-b-xl p-2">
        <div className="h-1 bg-linear-to-r from-blue-200 via-purple-200 to-pink-200 rounded-full" />
      </div>
    </div>
  );
}
